import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Paper,
  Pagination,
} from "@mui/material";
import { RichTextDisplay } from "@/components/common";
import {
  PlayArrow as StartIcon,
  ExpandMore as ExpandMoreIcon,
  Book as BookIcon,
  VolumeUp as VolumeUpIcon,
  Assignment as AssignmentIcon,
  Quiz as QuizIcon,
  CheckCircle as CompletedIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useGetExercisesQuery,
  useGetStudentExercisesQuery,
  useGetStudentProgressQuery,
  useLazyGetExercisesQuery,
  useLazyGetStudentExercisesQuery,
} from "@/store/api/user/exerciseApi";
import { useGetAdminExercisesQuery, useLazyGetAdminExercisesQuery } from "@/store/api/admin/exerciseApi";
import { selectIsAdmin } from "@/store/slices/authSlice";
import { useSelector } from "react-redux";
import { EXERCISE_PART_TYPES as PART_TYPES } from '@/constants/exerciseConstants';

const MyExercises = () => {
  const navigate = useNavigate();
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 9;

  const isAdmin = useSelector(selectIsAdmin);

  // Call both hooks unconditionally, but skip the one we don't need
  const { 
    data: adminExercisesResponse, 
    isLoading: isAdminLoading, 
    error: adminError 
  } = useGetAdminExercisesQuery({ page, limit }, { skip: !isAdmin });
  
  const { 
    data: studentExercisesResponse, 
    isLoading: isStudentLoading, 
    error: studentError 
  } = useGetStudentExercisesQuery({ page, limit }, { skip: isAdmin });

  const { data: progressResponse } = useGetStudentProgressQuery(
    selectedExercise
      ? {
          exerciseId: selectedExercise.exerciseId,
          dayId: selectedExercise.dayId,
        }
      : undefined,
    { skip: !selectedExercise }
  );

  // Use the appropriate response based on user role
  const exercisesResponse = isAdmin ? adminExercisesResponse : studentExercisesResponse;
  const isLoading = isAdmin ? isAdminLoading : isStudentLoading;
  const error = isAdmin ? adminError : studentError;

  const exercises = exercisesResponse?.data?.exercises || exercisesResponse?.data || [];
  const totalPages = exercisesResponse?.data?.pagination?.totalPages || exercisesResponse?.pagination?.totalPages || 1;
  const totalCount = exercisesResponse?.data?.pagination?.total || exercisesResponse?.pagination?.total || 0;

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 200, behavior: 'smooth' });
  };

  const progressData = progressResponse?.data;

  const handleViewProgress = (exercise) => {
    setSelectedExercise(exercise);
    setProgressDialogOpen(true);
  };

  const handleStartExercise = (exerciseId) => {
    console.log('exerciseId', exerciseId);
    navigate(`/exercise/${exerciseId}`);
  };

  const getCompletionPercentage = (progress) => {
    if (!progress) return 0;
    const totalParts = Object.keys(PART_TYPES).length;
    const completedParts = Object.values(progress.partProgress || {}).filter(
      Boolean
    ).length;
    return Math.round((completedParts / totalParts) * 100);
  };

  const getPartStatusChips = (day, progress) => {
    return Object.entries(PART_TYPES)
      .map(([key, config]) => {
        if (key === "description" || day?.[key]) {
          const isCompleted = progress?.partProgress?.[key];
          const IconComponent = config.icon;

          return (
            <Chip
              key={key}
              icon={<IconComponent />}
              label={config.name}
              size="small"
              color={isCompleted ? "success" : config.color}
              variant={isCompleted ? "filled" : "outlined"}
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          );
        }
        return null;
      })
      .filter(Boolean);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load exercises: {error?.data?.message || error.message}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #f0f4f8 0%, #e1e8ed 50%, #e3f2fd 100%)",
        py: 2,
      }}
    >
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box
          sx={{
            mb: 4,
            textAlign: "center",
            p: 4,
            borderRadius: 4,
            background: "white",
            backdropFilter: "blur(20px)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 3 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                background:
                  "linear-gradient(135deg, #2196F3 0%, #1976D2 50%, #1565C0 100%)",
                mr: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BookIcon sx={{ color: "white", fontSize: 32 }} />
            </Box>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                background:
                  "linear-gradient(135deg, #2196F3 0%, #1976D2 50%, #1565C0 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Recording and Homework
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: "#2c3e50",
              fontWeight: 500,
              opacity: 0.8,
            }}
          >
            Complete your assigned exercises to improve your TOEIC skills
          </Typography>
        </Box>

        {/* Exercises Section */}
        <Paper
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 4,
            background: "white",
            backdropFilter: "blur(20px)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <Grid container spacing={4}>
            {exercises?.map((exercise) => (
              <Grid item xs={12} key={`${exercise._id}`}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    background: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={2}
                  >
                    <Box flexGrow={1}>
                      <Typography
                        variant="h5"
                        component="h2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        {exercise.title}
                      </Typography>
                      <Typography variant="h6" color="primary" gutterBottom>
                        {exercise.dayTitle}
                      </Typography>
                      {exercise.description && (
                        <Box sx={{ mb: 2 }}>
                          <RichTextDisplay content={exercise.description} />
                        </Box>
                      )}
                    </Box>
                    <Box textAlign="center" minWidth={120}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Progress
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {getCompletionPercentage(exercise.progress)}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={getCompletionPercentage(exercise.progress)}
                        sx={{
                          mt: 1,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: "rgba(33, 150, 243, 0.1)",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 4,
                            background:
                              "linear-gradient(90deg, #2196F3 0%, #1976D2 100%)",
                          },
                        }}
                      />
                    </Box>
                  </Box>

                {/* Parts */}
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Available Parts:
                  </Typography>
                  <Box>
                    {getPartStatusChips(exercise.day, exercise.progress)}
                  </Box>
                </Box>

                {/* Assignment Info */}
                <Box display="flex" alignItems="center" mb={2}>
                  <ScheduleIcon sx={{ mr: 1, color: "text.secondary" }} />
                  <Typography variant="body2" color="text.secondary">
                    Assigned:{" "}
                    {new Date(exercise.assignedAt).toLocaleDateString()}
                  </Typography>
                  {exercise.progress?.completedAt && (
                    <>
                      <CompletedIcon
                        sx={{ ml: 2, mr: 1, color: "success.main" }}
                      />
                      <Typography variant="body2" color="success.main">
                        Completed:{" "}
                        {new Date(
                          exercise.progress.completedAt
                        ).toLocaleDateString()}
                      </Typography>
                    </>
                  )}
                </Box>
              </CardContent>

                <CardActions sx={{ p: 2 }}>
                  <Button
                    fullWidth
                    startIcon={<StartIcon />}
                    variant="contained"
                    onClick={() => handleStartExercise(exercise._id)}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "1rem",
                      background:
                        "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #1976D2 0%, #1565C0 100%)",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
                      },
                      mr: 1,
                    }}
                  >
                    {exercise.progress?.isCompleted ? "Review" : "Start Learning"}
                  </Button>
                  {/* <Button
                    variant="outlined"
                    onClick={() => handleViewProgress(exercise)}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "1rem",
                      borderColor: "#2196F3",
                      color: "#2196F3",
                      "&:hover": {
                        borderColor: "#1976D2",
                        backgroundColor: "rgba(33, 150, 243, 0.05)",
                      },
                    }}
                  >
                    View Progress
                  </Button> */}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                Total: {totalCount} exercises
              </Typography>
            </Box>
          )}

          {/* Empty State */}
          {exercises?.length === 0 && (
            <Grid item xs={12}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 3,
                  background: "rgba(255,255,255,0.9)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  py: 8,
                  px: 4,
                  textAlign: "center",
                  transition: "all 0.3s ease",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      p: 3,
                      mb: 2,
                      borderRadius: "50%",
                      background: "rgba(33, 150, 243, 0.1)",
                      width: 80,
                      height: 80,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                    }}
                  >
                    <BookIcon sx={{ fontSize: 40, color: "#2196F3" }} />
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{ mb: 2, color: "#546e7a", fontWeight: 600 }}
                  >
                    No exercises assigned yet
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ maxWidth: 500, mx: "auto", mb: 4 }}
                  >
                    Your teacher will assign exercises for you to complete. Check back
                    soon or explore our available resources.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Paper>

        {/* Progress Dialog */}
        <Dialog
          open={progressDialogOpen}
          onClose={() => setProgressDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Exercise Progress</DialogTitle>
          <DialogContent>
            {selectedExercise && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {selectedExercise.title} - {selectedExercise.dayTitle}
                </Typography>

                <Box mb={3}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Overall Progress
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={getCompletionPercentage(selectedExercise.progress)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: "rgba(33, 150, 243, 0.1)",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 4,
                        background:
                          "linear-gradient(90deg, #2196F3 0%, #1976D2 100%)",
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {getCompletionPercentage(selectedExercise.progress)}% Complete
                  </Typography>
                </Box>

                <Typography variant="subtitle1" gutterBottom>
                  Parts Progress:
                </Typography>

                {Object.entries(PART_TYPES).map(([key, config]) => {
                  if (key === "description" || selectedExercise.day[key]) {
                    const isCompleted =
                      selectedExercise.progress?.partProgress?.[key];
                    const IconComponent = config.icon;

                    return (
                      <Box key={key} display="flex" alignItems="center" mb={1}>
                        <IconComponent
                          sx={{ mr: 2, color: `${config.color}.main` }}
                        />
                        <Typography variant="body1" sx={{ minWidth: 150 }}>
                          {config.name}
                        </Typography>
                        <Chip
                          label={isCompleted ? "Completed" : "Not Started"}
                          color={isCompleted ? "success" : "default"}
                          size="small"
                        />
                      </Box>
                    );
                  }
                  return null;
                })}

                {selectedExercise.progress?.scores && (
                  <Box mt={3}>
                    <Typography variant="subtitle1" gutterBottom>
                      Scores:
                    </Typography>
                    {Object.entries(selectedExercise.progress.scores).map(
                      ([partType, score]) => (
                        <Typography key={partType} variant="body2">
                          {PART_TYPES[partType]?.name}: {score}%
                        </Typography>
                      )
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setProgressDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default MyExercises;
